import { Pencil, Plus, Trash2 } from "lucide-react";
import { useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { Field } from "../SimpleSelect";
import { PLACEHOLDERS, type QuickReply } from "./quickReplies";
import { deleteQuickReply, saveQuickReply } from "./useQuickReplies";

interface Draft {
  id?: Identifier;
  label: string;
  body: string;
  forTeam: boolean;
}

interface QuickRepliesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  replies: QuickReply[];
  meId?: Identifier;
  isAdmin: boolean;
  onSaved: () => void;
}

const ReplyRow = ({
  reply,
  canEdit,
  onEdit,
  onDelete,
}: {
  reply: QuickReply;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <li className="flex items-start gap-2 rounded-lg border p-2 text-sm">
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="font-medium">{reply.label}</span>
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {reply.body}
      </span>
    </div>
    {canEdit ? (
      <>
        <Button size="icon" variant="ghost" title="Editar" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" title="Borrar" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </>
    ) : null}
  </li>
);

/** Add, edit and delete saved replies: your own, and the team's for admins. */
export const QuickRepliesDialog = ({
  open,
  onOpenChange,
  replies,
  meId,
  isAdmin,
  onSaved,
}: QuickRepliesDialogProps) => {
  const notify = useNotify();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const team = replies.filter((reply) => reply.owner_id == null);
  const mine = replies.filter((reply) => reply.owner_id != null);

  const run = async (action: () => Promise<void>, done: string) => {
    setSaving(true);
    try {
      await action();
      notify(done, { type: "success" });
      onSaved();
      setDraft(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!draft || !draft.label.trim() || !draft.body.trim()) return;
    run(
      () =>
        saveQuickReply({
          id: draft.id,
          label: draft.label,
          body: draft.body,
          ownerId: draft.forTeam ? null : (meId ?? null),
          sort:
            draft.id == null
              ? (draft.forTeam ? team : mine).length + 1
              : undefined,
        }),
      "Respuesta guardada",
    );
  };

  const edit = (reply: QuickReply) =>
    setDraft({
      id: reply.id,
      label: reply.label,
      body: reply.body,
      forTeam: reply.owner_id == null,
    });

  const remove = (reply: QuickReply) =>
    run(() => deleteQuickReply(reply.id), "Respuesta borrada");

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) setDraft(null);
        onOpenChange(value);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Respuestas guardadas</DialogTitle>
          <DialogDescription>
            Las del equipo las ve todo el mundo. Las tuyas solo las ves tú.
          </DialogDescription>
        </DialogHeader>

        {draft ? (
          <div className="flex flex-col gap-3">
            <Field label="Nombre del botón">
              <Input
                value={draft.label}
                maxLength={40}
                onChange={(event) =>
                  setDraft({ ...draft, label: event.target.value })
                }
                placeholder="Ej.: Seguimiento a los 2 días"
              />
            </Field>
            <Field label="Mensaje">
              <Textarea
                value={draft.body}
                rows={5}
                maxLength={1000}
                onChange={(event) =>
                  setDraft({ ...draft, body: event.target.value })
                }
                placeholder="Hola {nombre}, soy {agente} de Gula de Agua…"
              />
            </Field>
            <p className="text-xs text-muted-foreground">{PLACEHOLDERS}</p>
            {isAdmin && draft.id == null ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.forTeam}
                  onCheckedChange={(value) =>
                    setDraft({ ...draft, forTeam: value === true })
                  }
                />
                Para todo el equipo
              </label>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDraft(null)}>
                Cancelar
              </Button>
              <Button
                disabled={saving || !draft.label.trim() || !draft.body.trim()}
                onClick={save}
              >
                Guardar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Button
              className="self-start"
              onClick={() => setDraft({ label: "", body: "", forTeam: false })}
            >
              <Plus className="h-4 w-4" />
              Nueva respuesta
            </Button>
            <section className="flex flex-col gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Del equipo
              </h3>
              <ul className="flex flex-col gap-2">
                {team.map((reply) => (
                  <ReplyRow
                    key={reply.id}
                    reply={reply}
                    canEdit={isAdmin}
                    onEdit={() => edit(reply)}
                    onDelete={() => remove(reply)}
                  />
                ))}
              </ul>
            </section>
            <section className="flex flex-col gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Mías
              </h3>
              {mine.length ? (
                <ul className="flex flex-col gap-2">
                  {mine.map((reply) => (
                    <ReplyRow
                      key={reply.id}
                      reply={reply}
                      canEdit
                      onEdit={() => edit(reply)}
                      onDelete={() => remove(reply)}
                    />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Todavía no tienes respuestas propias.
                </p>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
